/**
 * Rental tab — long-term hold metrics. Mockup tile order:
 *   Monthly Rent · Rent/Sqft
 *   Rent/Bedroom · 1% Rule
 *   Monthly Cash Flow (+ "View breakdown →" link) · DSCR
 *   Cash-on-Cash · GRM
 */
import { ArrowRight, Info } from "lucide-react";

import { fmtMoney, fmtPct } from "@/lib/calculators";

import { InfoTip } from "../InfoTip";
import type { DealInputsState, DerivedMetrics } from "../detail-helpers";
import { MetricTile } from "./panel-primitives";

interface RentAnalysisProps {
  state: DealInputsState;
  metrics: DerivedMetrics;
  /** Numeric sqft so we can derive rent / sqft. */
  sqft: number;
  /** Number of bedrooms — drives the Rent/Bedroom tile. */
  bedrooms?: number | null;
  /** Click handler for the "View breakdown →" link (jumps to Cash Flow tab). */
  onViewCashflowBreakdown?: () => void;
}

export function RentAnalysis({
  state,
  metrics,
  sqft,
  bedrooms,
  onViewCashflowBreakdown,
}: RentAnalysisProps) {
  const rentPerSqft = sqft > 0 ? metrics.monthlyRent / sqft : 0;
  const bdCount = bedrooms ?? 0;
  const rentPerBd = bdCount > 0 ? Math.round(metrics.monthlyRent / bdCount) : 0;

  return (
    <div className="rounded-lg border border-border bg-card p-4">
      <h3 className="mb-3 flex items-center gap-2 text-sm font-semibold">
        <Info className="h-4 w-4 text-primary" aria-hidden />
        Rent Analysis
      </h3>
      <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
        <MetricTile
          label="Monthly Rent"
          value={fmtMoney(metrics.monthlyRent) + "/mo"}
          sub="Estimated market rent"
        />
        <MetricTile
          label="Rent / Sqft"
          value={sqft > 0 ? rentPerSqft.toFixed(2) : "—"}
          sub="$ per sqft / mo"
        />
        <MetricTile
          label="Rent / Bedroom"
          value={bdCount > 0 ? fmtMoney(rentPerBd) : "—"}
          sub={bdCount > 0 ? `${bdCount} bd` : "—"}
        />
        <MetricTile
          label="1% Rule"
          value={fmtPct(metrics.onePctValue, 2)}
          sub="Rent ÷ Price · ≥ 1% ideal"
          tone={metrics.onePctValue >= 1 ? "pos" : "neg"}
        />

        {/* Monthly Cash Flow tile with "View breakdown →" link */}
        <div className="rounded-md border border-border bg-muted/40 p-3">
          <div className="flex items-center justify-between gap-2">
            <span className="text-[11px] text-muted-foreground">
              Monthly Cash Flow
            </span>
            {onViewCashflowBreakdown && (
              <button
                type="button"
                onClick={onViewCashflowBreakdown}
                className="inline-flex items-center gap-1 text-[11px] font-semibold text-primary hover:underline"
                aria-label="View cash flow breakdown"
              >
                View breakdown
                <ArrowRight className="h-3 w-3" aria-hidden />
              </button>
            )}
          </div>
          <div
            className={`mt-0.5 text-lg font-extrabold leading-tight ${
              metrics.cashflow.monthlyNet >= 0 ? "text-success" : "text-destructive"
            }`}
          >
            {metrics.cashflow.monthlyNet < 0 ? "−" : ""}
            {fmtMoney(Math.abs(metrics.cashflow.monthlyNet))}
          </div>
          <div className="mt-0.5 text-[11px] text-muted-foreground">
            NOI − Loan Payment
          </div>
        </div>
        <MetricTile
          label="DSCR"
          value={metrics.dscrValue.toFixed(2)}
          sub="NOI ÷ Loan Payment"
          tone={
            metrics.dscrValue >= 1.2
              ? "pos"
              : metrics.dscrValue >= 1
                ? "dim"
                : "neg"
          }
        />

        {/* Cash-on-Cash with info tooltip */}
        <div className="rounded-md border border-border bg-muted/40 p-3">
          <div className="flex items-center gap-1.5">
            <span className="text-[11px] text-muted-foreground">Cash-on-Cash</span>
            <InfoTip text="Annual cash flow ÷ cash actually invested (down + closing + rehab). Measures % return on the cash you put in." />
          </div>
          <div
            className={`mt-0.5 text-lg font-extrabold leading-tight ${
              metrics.cocPct >= 8
                ? "text-success"
                : metrics.cocPct >= 0
                  ? ""
                  : "text-destructive"
            }`}
          >
            {fmtPct(metrics.cocPct, 2)}
          </div>
          <div className="mt-0.5 text-[11px] text-muted-foreground">
            Annual CF ÷ Cash Invested
          </div>
        </div>

        {/* GRM with info tooltip */}
        <div className="rounded-md border border-border bg-muted/40 p-3">
          <div className="flex items-center gap-1.5">
            <span className="text-[11px] text-muted-foreground">GRM</span>
            <InfoTip text="Gross Rent Multiplier = Price ÷ Annual Gross Rent. Lower is better. <7 great · 7–10 solid · 10–14 typical · >15 expensive." />
          </div>
          <div
            className={`mt-0.5 text-lg font-extrabold leading-tight ${
              metrics.grmValue > 0 && metrics.grmValue <= 10
                ? "text-success"
                : metrics.grmValue > 14
                  ? "text-destructive"
                  : ""
            }`}
          >
            {metrics.grmValue > 0 ? metrics.grmValue.toFixed(2) : "—"}
          </div>
          <div className="mt-0.5 text-[11px] text-muted-foreground">
            Price ÷ Annual Rent · ≤ 10 ideal
          </div>
        </div>
      </div>
    </div>
  );
}
